import React, { useEffect, useState } from "react"
import {
  Row, Col, CardHeader, CardBody, Spinner, Alert, Input, Button,
  Modal, ModalHeader, ModalBody, ModalFooter,
  FormGroup, Label, FormFeedback,
} from "reactstrap"
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table"
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css"
import Select from "react-select"
import { useFormik } from "formik"
import * as Yup from "yup"
import { toast } from "react-toastify"
import {
  adminGetPriceOverrides,
  adminCreatePriceOverride,
  adminUpdatePriceOverride,
  adminDeletePriceOverride,
  adminGetPlans,
} from "../../api/requests/subscription-requests"
import { fetchCompaniesNames } from "../../api/requests/account-requests"
import { SubscriptionDeleteModal } from "../modals/subscription/SubscriptionDeleteModal"

const TableCard = ({ className = "", children, ...rest }) => (
  <div
    className={`card rounded-4 border-0 shadow-lg ${className}`}
    style={{ boxShadow: "0 .5rem 1.25rem rgba(10,49,110,.08)" }}
    {...rest}
  >
    {children}
  </div>
)

const NAVY = "#221C5D"

const PriceOverrides = () => {
  const [overrides, setOverrides] = useState([])
  const [plans, setPlans] = useState([])
  const [companies, setCompanies] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [modalOpen, setModalOpen] = useState(false)
  const [editingOverride, setEditingOverride] = useState(null)
  const [saving, setSaving] = useState(false)
  const [deleteOverride, setDeleteOverride] = useState(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [priceDisplay, setPriceDisplay] = useState("")

  // Sayıyı TR biçimine çevir (53232 → "53.232,00")
  const formatPriceInput = (num) => {
    if (num == null || num === "") return ""
    const n = typeof num === "string" ? parseFloat(num) : num
    if (isNaN(n)) return ""
    return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)
  }

  // TR biçimli metni sayıya çevir ("53.232,00" → 53232). Nokta binlik ayracı,
  // virgül ondalık: biçimlendirme ile ayrıştırma aynı dili konuşmak zorunda,
  // aksi halde kaydedilen tutar ekranda yazandan farklı olur.
  const parsePriceInput = (str) => {
    if (!str) return ""
    const cleaned = String(str).replace(/\./g, "").replace(",", ".")
    const num = parseFloat(cleaned)
    return isNaN(num) ? "" : num
  }

  const handlePriceChange = (e) => {
    let raw = e.target.value
    // Sadece rakam, nokta ve virgüle izin ver
    raw = raw.replace(/[^0-9.,]/g, "")
    setPriceDisplay(raw)
    formik.setFieldValue("overridePrice", parsePriceInput(raw))
  }

  const handlePriceBlur = () => {
    formik.setFieldTouched("overridePrice", true)
    const num = parsePriceInput(priceDisplay)
    if (num !== "" && !isNaN(num)) {
      setPriceDisplay(formatPriceInput(num))
    }
  }

  const styles = `
    .overrides-modern .table thead tr { background-color: #f9f9f9; }
    .overrides-modern .table th { border: 0; color: #6c757d; font-weight: 600; }
    .overrides-modern .table td { vertical-align: middle; }
    .overrides-modern .table-hover tbody tr:hover { background: #fcfcfd; }
    .overrides-modern .search-input { width: 250px; }
    .overrides-modern .header-actions { gap: .5rem; }
    .overrides-modern .btn-rounded { border-radius: 999px; }
    .overrides-modern .btn-navy { background: ${NAVY}; border-color: ${NAVY}; color: #fff; }
    .overrides-modern .btn-navy:hover { filter: brightness(1.03); }
    .overrides-modern .action-btn { border-radius: 999px; padding: .35rem .6rem; }
    .overrides-modern .pill-input { border-radius: 999px !important; padding: .45rem .9rem; }
    @media (max-width: 576px) {
      .overrides-modern .search-input { width: 100%; }
      .overrides-modern .table { font-size: 12px; }
      .overrides-modern .table td, .overrides-modern .table th { padding: .5rem; }
    }
  `

  document.title = "Fiyat Müdahaleleri | Tahsilat360"

  const loadData = async (first = false) => {
    try {
      setError(null)
      if (first) setLoading(true); else setRefreshing(true)
      const [overridesRes, plansRes, companiesRes] = await Promise.all([
        adminGetPriceOverrides(),
        adminGetPlans(),
        fetchCompaniesNames(),
      ])
      setOverrides(overridesRes.data || [])
      setPlans(plansRes.data || [])
      setCompanies(companiesRes.data || companiesRes || [])
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }

  useEffect(() => {
    loadData(true)
  }, [])

  const companyOptions = companies.map((c) => ({ value: c.id, label: c.name || c.companyName }))
  const planOptions = plans.map((p) => ({ value: p.id, label: p.name }))

  const getCompanyName = (companyId) => {
    const c = companies.find((co) => co.id === companyId)
    return c?.name || c?.companyName || companyId
  }
  const getPlanName = (planId) => {
    const p = plans.find((pl) => pl.id === planId)
    return p?.name || planId
  }

  const filteredOverrides = overrides.filter((o) => {
    if (!searchTerm) return true
    const s = searchTerm.toLowerCase()
    return (
      getCompanyName(o.companyId)?.toLowerCase().includes(s) ||
      getPlanName(o.planId)?.toLowerCase().includes(s) ||
      o.reason?.toLowerCase().includes(s)
    )
  })

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      companyId: editingOverride?.companyId ?? "",
      planId: editingOverride?.planId ?? "",
      overridePrice: editingOverride?.overridePrice ?? "",
      reason: editingOverride?.reason || "",
    },
    validationSchema: Yup.object({
      companyId: Yup.mixed().required("Firma seçimi gerekli"),
      planId: Yup.mixed().required("Paket seçimi gerekli"),
      overridePrice: Yup.number().min(0).required("Özel fiyat gerekli"),
      reason: Yup.string().required("Sebep gerekli"),
    }),
    onSubmit: async (values) => {
      setSaving(true)
      try {
        if (editingOverride) {
          await adminUpdatePriceOverride(editingOverride.id, values)
          toast.success("Fiyat müdahalesi güncellendi")
        } else {
          await adminCreatePriceOverride(values)
          toast.success("Fiyat müdahalesi oluşturuldu")
        }
        closeModal()
        loadData()
      } catch (err) {
        toast.error(err.message)
      } finally {
        setSaving(false)
      }
    },
  })

  const handleDelete = async (id) => {
    try {
      await adminDeletePriceOverride(id)
      toast.success("Fiyat müdahalesi silindi")
      loadData()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setDeleteModalOpen(false)
      setDeleteOverride(null)
    }
  }

  const openCreate = () => { setEditingOverride(null); formik.resetForm(); setPriceDisplay(""); setModalOpen(true) }
  const openEdit = (override) => {
    setEditingOverride(override)
    setPriceDisplay(override?.overridePrice != null ? formatPriceInput(override.overridePrice) : "")
    setModalOpen(true)
  }
  const closeModal = () => { setModalOpen(false); setEditingOverride(null); formik.resetForm(); setPriceDisplay("") }

  const formatPrice = (price) =>
    new Intl.NumberFormat("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price) + " ₺"

  return (
    <div className="page-content overrides-modern">
      <style>{styles}</style>
      <div className="container-fluid">
        <Row>
          <Col>
            <TableCard>
              <CardHeader className="bg-white border-0 rounded-top-4">
                <div className="d-none d-md-block">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div>
                      <h5 className="mb-1 fw-bold text-dark">Fiyat Müdahaleleri</h5>
                      <p className="mb-0 text-muted" style={{ fontSize: ".9rem" }}>
                        {overrides.length > 0 ? `Toplam ${overrides.length} müdahale` : "Müdahale bulunamadı"}
                      </p>
                    </div>
                    <Button className="d-flex align-items-center gap-1 btn-rounded btn-navy" onClick={openCreate}>
                      <i className="bx bx-plus" /> Yeni Müdahale
                    </Button>
                  </div>
                  <div className="d-flex justify-content-end">
                    <Input type="search" placeholder="Ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="search-input pill-input" />
                  </div>
                </div>
                <div className="d-md-none">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0 fw-semibold">Fiyat Müdahaleleri</h5>
                    <Button size="sm" className="d-flex align-items-center gap-1 btn-rounded btn-navy" onClick={openCreate}>
                      <i className="bx bx-plus" /> Ekle
                    </Button>
                  </div>
                  <Input type="search" placeholder="Ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pill-input mb-2" />
                </div>
              </CardHeader>

              <CardBody className="pt-2">
                {error && (
                  <Alert color="danger" className="mb-3 rounded-4">
                    <i className="bx bx-error-circle me-2" />{error}
                    <button className="btn btn-sm btn-outline-danger ms-2 btn-rounded" onClick={() => loadData()}>Tekrar Dene</button>
                  </Alert>
                )}

                {loading ? (
                  <div className="text-center p-5">
                    <Spinner size="lg" color="primary" />
                    <h6 className="mt-3 text-muted">Veriler yükleniyor...</h6>
                  </div>
                ) : (
                  <>
                    <div className={`d-flex justify-content-between align-items-center mb-2 ${refreshing ? "opacity-50" : ""}`}>
                      <div className="text-muted" style={{ fontSize: ".9rem" }}>
                        {filteredOverrides.length > 0 && <>Toplam <strong>{filteredOverrides.length}</strong> kayıt</>}
                      </div>
                      <button className="btn btn-light btn-sm btn-rounded" onClick={() => loadData()} title="Yenile">
                        <i className={`bx bx-refresh ${refreshing ? "bx-spin" : ""}`} />
                      </button>
                    </div>

                    <div className="table-responsive rounded-4 border">
                      <Table className="table table-hover mb-0">
                        <Thead>
                          <Tr>
                            <Th>Firma</Th>
                            <Th>Paket</Th>
                            <Th>Özel Fiyat</Th>
                            <Th className="d-none d-md-table-cell">Sebep</Th>
                            <Th className="text-end">İşlemler</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {filteredOverrides.length ? filteredOverrides.map((o) => (
                            <Tr key={o.id} className="border-bottom">
                              <Td className="fw-semibold text-dark">{getCompanyName(o.companyId)}</Td>
                              <Td>{getPlanName(o.planId)}</Td>
                              <Td className="fw-semibold" style={{ color: NAVY }}>{formatPrice(o.overridePrice)}</Td>
                              <Td className="d-none d-md-table-cell text-muted small">{o.reason}</Td>
                              <Td className="text-end">
                                <div className="d-flex gap-1 justify-content-end flex-wrap">
                                  <button className="btn btn-sm action-btn btn-navy" title="Düzenle" onClick={() => openEdit(o)}>
                                    <i className="bx bx-edit" />
                                  </button>
                                  <button className="btn btn-sm action-btn" style={{ background: "#dc3545", borderColor: "#dc3545", color: "#fff" }} title="Sil" onClick={() => { setDeleteOverride(o); setDeleteModalOpen(true) }}>
                                    <i className="bx bx-trash" />
                                  </button>
                                </div>
                              </Td>
                            </Tr>
                          )) : (
                            <Tr>
                              <Td colSpan="5" className="text-center py-4">
                                <div className="text-muted">
                                  <i className="bx bx-dollar-circle display-4 d-block mb-2" />
                                  Fiyat müdahalesi bulunamadı
                                  {searchTerm && <div className="mt-2"><small>Arama teriminizi değiştirmeyi deneyin</small></div>}
                                </div>
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </div>
                  </>
                )}
              </CardBody>
            </TableCard>
          </Col>
        </Row>
      </div>

      {/* Create/Edit Modal */}
      <Modal isOpen={modalOpen} toggle={closeModal}>
        <ModalHeader toggle={closeModal}>
          {editingOverride ? "Fiyat Müdahalesini Düzenle" : "Yeni Fiyat Müdahalesi"}
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>Firma</Label>
            <Select
              options={companyOptions}
              value={companyOptions.find((o) => String(o.value) === String(formik.values.companyId)) || null}
              onChange={(opt) => { formik.setFieldValue("companyId", opt?.value ?? ""); formik.setFieldTouched("companyId", true, false) }}
              placeholder="Firma seçin..."
              isClearable
              noOptionsMessage={() => "Firma bulunamadı"}
            />
            {formik.touched.companyId && formik.errors.companyId && (
              <div className="text-danger small mt-1">{formik.errors.companyId}</div>
            )}
          </FormGroup>
          <FormGroup>
            <Label>Paket</Label>
            <Select
              options={planOptions}
              value={planOptions.find((o) => String(o.value) === String(formik.values.planId)) || null}
              onChange={(opt) => { formik.setFieldValue("planId", opt?.value ?? ""); formik.setFieldTouched("planId", true, false) }}
              placeholder="Paket seçin..."
              isClearable
              noOptionsMessage={() => "Paket bulunamadı"}
            />
            {formik.touched.planId && formik.errors.planId && (
              <div className="text-danger small mt-1">{formik.errors.planId}</div>
            )}
          </FormGroup>
          <FormGroup>
            <Label>Özel Fiyat (TL)</Label>
            <Input
              type="text" name="overridePrice"
              placeholder="0,00"
              value={priceDisplay}
              onChange={handlePriceChange}
              onBlur={handlePriceBlur}
              invalid={formik.touched.overridePrice && !!formik.errors.overridePrice}
            />
            <FormFeedback>{formik.errors.overridePrice}</FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label>Sebep</Label>
            <Input
              type="textarea" name="reason" rows={2}
              value={formik.values.reason}
              onChange={formik.handleChange} onBlur={formik.handleBlur}
              invalid={formik.touched.reason && !!formik.errors.reason}
              placeholder="Özel anlaşmaya istinaden..."
            />
            <FormFeedback>{formik.errors.reason}</FormFeedback>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeModal}>İptal</Button>
          <Button color="primary" disabled={saving} onClick={formik.handleSubmit}>
            {saving ? <Spinner size="sm" /> : editingOverride ? "Güncelle" : "Oluştur"}
          </Button>
        </ModalFooter>
      </Modal>

      <SubscriptionDeleteModal
        isOpen={deleteModalOpen}
        toggle={() => { setDeleteModalOpen(false); setDeleteOverride(null) }}
        title="Fiyat Müdahalesi Silme Onayı"
        icon="bx-dollar-circle"
        message="Aşağıdaki fiyat müdahalesini kalıcı olarak silmek istediğinizden emin misiniz?"
        item={deleteOverride}
        renderInfo={(o) => (
          <div>
            <h6 className="mb-1 text-dark">{getCompanyName(o.companyId)}</h6>
            <div className="text-muted small">
              {getPlanName(o.planId)} - {formatPrice(o.overridePrice)}
            </div>
            {o.reason && <div className="text-muted small mt-1">Sebep: {o.reason}</div>}
          </div>
        )}
        onConfirm={handleDelete}
      />
    </div>
  )
}

export default PriceOverrides
